import React, { useMemo, useState } from "react";
import { Card, message } from "antd";
import { Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { DashboardLoadingSkeleton } from "src/components/common";
import { deleteSocialListeningRun, type SocialListeningRun } from "src/services/SocialListeningAPIs";
import { getThrownApiDetail } from "src/utils/showApiDetailToast";
import "../Styles/History.scss";
import "../Styles/Shared.scss";
import BackButton from "../Components/BackButton";
import HistoryRow from "../Components/HistoryRow";
import StatusMessage from "../Components/StatusMessage";
import { useSocialListeningHistory } from "../Hooks/UseSocialListeningHistory";
import { getStoredId, SOCIAL_LISTENING_ROUTES } from "../Utils/SocialListeningConstants";

const HISTORY_KEY = "socialListingAgent.historyPage";

const hasReport = (run: SocialListeningRun) => run.status === "completed" || run.status === "partial";

const History: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { runs, setRuns, isLoading, hasError, reload } = useSocialListeningHistory();
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const selectableIds = useMemo(() => runs.map(run => run.run_id), [runs]);
  const isAllSelected =
    selectableIds.length > 0 && selectableIds.every(id => selectedIds.includes(id));

  const handleToggle = (runId: number, checked: boolean) =>
    setSelectedIds(prev => (checked ? [...prev, runId] : prev.filter(id => id !== runId)));

  const handleDelete = async () => {
    const userId = getStoredId("user_id");
    if (userId === null || selectedIds.length === 0) {return;}

    setIsDeleting(true);
    const results = await Promise.allSettled(
      selectedIds.map(runId => deleteSocialListeningRun(userId, runId))
    );
    const deletedIds = selectedIds.filter((_, index) => results[index].status === "fulfilled");
    const failure = results.find(
      (result): result is PromiseRejectedResult => result.status === "rejected"
    );

    setRuns(prev => prev.filter(run => !deletedIds.includes(run.run_id)));
    setSelectedIds(prev => prev.filter(id => !deletedIds.includes(id)));
    if (deletedIds.length) {
      message.success(t(`${HISTORY_KEY}.deleteSuccess`));
    }
    if (failure) {
      message.error(getThrownApiDetail(failure.reason) || t(`${HISTORY_KEY}.deleteFailed`));
    }
    setIsDeleting(false);
  };

  const handleDownload = () => {
    const readyIds = runs
      .filter(run => selectedIds.includes(run.run_id) && hasReport(run))
      .map(run => run.run_id);
    if (readyIds.length === 0) {
      message.warning(t(`${HISTORY_KEY}.downloadSelectWarning`));
      return;
    }
    if (readyIds.length < selectedIds.length) {
      message.info(
        t(`${HISTORY_KEY}.downloadSkipped`, "Reports that are still running or failed were skipped.")
      );
    }
    readyIds.forEach(runId => {
      window.open(`${SOCIAL_LISTENING_ROUTES.report(runId)}?download=1`, "_blank");
    });
  };

  const renderList = () => {
    if (isLoading) {
      return <DashboardLoadingSkeleton />;
    }
    return (
      <div className="history-list">
        {hasError && (
          <StatusMessage
            tone="error"
            title={t(`${HISTORY_KEY}.loadFailedTitle`, "Reports unavailable")}
            message={t(`${HISTORY_KEY}.loadFailed`, "We couldn't load your reports. Please try again.")}
            action={{ label: t(`${HISTORY_KEY}.retry`, "Try again"), onClick: reload }}
          />
        )}
        {runs.map(run => (
          <HistoryRow
            key={run.run_id}
            run={run}
            isSelected={selectedIds.includes(run.run_id)}
            onToggle={handleToggle}
            onView={() => navigate(SOCIAL_LISTENING_ROUTES.report(run.run_id))}
          />
        ))}
        {!hasError && runs.length === 0 && (
          <div className="recent-report-empty">{t(`${HISTORY_KEY}.noReports`)}</div>
        )}
      </div>
    );
  };

  return (
    <section className="history-page">
      <div className="history-header">
        <BackButton label={t(`${HISTORY_KEY}.back`)} to={SOCIAL_LISTENING_ROUTES.home} />
        <h1 className="history-heading">{t(`${HISTORY_KEY}.title`)}</h1>
      </div>

      <Card className="history-card" bordered={false}>
        <div className="history-card-header">
          <div className="history-header-select">
            <input
              className="history-checkbox"
              type="checkbox"
              checked={isAllSelected}
              disabled={selectableIds.length === 0}
              onChange={event => setSelectedIds(event.target.checked ? selectableIds : [])}
            />
          </div>

          <div className="history-actions">
            <Tippy content={t(`${HISTORY_KEY}.deleteTooltip`)} placement="top">
              <span className="history-icon-tooltip-trigger">
                <button
                  className="history-icon-button"
                  type="button"
                  aria-label={t(`${HISTORY_KEY}.deleteSelectedAria`)}
                  disabled={selectedIds.length === 0 || isDeleting}
                  onClick={handleDelete}
                >
                  <Trash2 size={20} className="history-delete-icon" />
                </button>
              </span>
            </Tippy>

            <Tippy content={t(`${HISTORY_KEY}.downloadTooltip`)} placement="top">
              <button
                className="history-icon-button"
                type="button"
                aria-label={t(`${HISTORY_KEY}.downloadAria`)}
                onClick={handleDownload}
              >
                <img src="/Icons/Downloads.svg" className="report-icon" alt="" />
              </button>
            </Tippy>
          </div>
        </div>

        {renderList()}
      </Card>
    </section>
  );
};

export default History;
